import { useResources } from '../hooks/useResources.ts';
import { formatNumber } from '../lib/utils.ts';
import { ResourceIcon } from './ResourceIcon';

export function ResourceBar() {
  const { resources, production, storage } = useResources();

  const items = [
    {
      key: 'metal',
      labelShort: 'MET',
      labelFull: 'Metall',
      color: 'text-metal-400',
      borderColor: 'border-metal-400/30',
      value: resources.metal,
      prod: production.metal,
      max: storage.metal,
    },
    {
      key: 'crystal',
      labelShort: 'KRI',
      labelFull: 'Kristall',
      color: 'text-crystal-400',
      borderColor: 'border-crystal-400/30',
      value: resources.crystal,
      prod: production.crystal,
      max: storage.crystal,
    },
    {
      key: 'deuterium',
      labelShort: 'DEU',
      labelFull: 'Deuterium',
      color: 'text-deuterium-400',
      borderColor: 'border-deuterium-400/30',
      value: resources.deuterium,
      prod: production.deuterium,
      max: storage.deuterium,
    },
  ] as const;

  const energyBalance = production.energy_produced - production.energy_consumed;
  const energyNegative = energyBalance < 0;

  return (
    <div className="flex items-center gap-1 md:gap-3 text-sm">
      {items.map(({ key, labelShort, labelFull, color, borderColor, value, prod, max }) => {
        const nearFull = value >= max * 0.99;
        return (
          <div
            key={key}
            className={`flex items-center gap-1.5 md:gap-2 md:px-3 md:py-1 md:rounded md:border ${borderColor} md:bg-space-800/60`}
            title={`Produktion: +${formatNumber(prod)}/Tick | Lager: ${formatNumber(value)} / ${formatNumber(max)}`}
          >
            {/* Resource icon — always visible */}
            <ResourceIcon
              type={key}
              size={15}
              className={`shrink-0 ${nearFull ? 'text-red-400' : color}`}
            />

            {/* Full label — desktop only */}
            <span className={`font-semibold text-xs uppercase tracking-wider ${color} hidden md:inline`}>
              {labelFull}
            </span>

            {/* Value */}
            <span className={`font-mono text-sm font-medium ${nearFull ? 'text-red-400' : 'text-slate-200'}`}>
              {formatNumber(value)}
            </span>

            {/* Production rate — desktop only */}
            <span className={`hidden md:inline font-mono text-xs ${color} opacity-70`}>
              +{formatNumber(prod)}/T
            </span>
          </div>
        );
      })}

      {/* Energie */}
      <div
        className={`flex items-center gap-1.5 md:gap-2 md:px-3 md:py-1 md:rounded md:border md:border-energy-400/30 md:bg-space-800/60`}
        title={`Produziert: ${formatNumber(production.energy_produced)} | Verbraucht: ${formatNumber(production.energy_consumed)}`}
      >
        <ResourceIcon
          type="energy"
          size={15}
          className={`shrink-0 ${energyNegative ? 'text-red-400' : 'text-energy-400'}`}
        />
        <span className="font-semibold text-xs uppercase tracking-wider text-energy-400 hidden md:inline">
          Energie
        </span>
        <span className={`font-mono text-sm font-medium ${energyNegative ? 'text-red-400' : 'text-energy-400'}`}>
          {energyNegative ? '' : '+'}{formatNumber(energyBalance)}
        </span>
      </div>
    </div>
  );
}
